import { Box, Button, Typography } from "@mui/material";
import type { ComponentType, Dispatch, SetStateAction } from "react";
import { useThemeContext } from "../../../globalContext/global-context";
import { OverlaySlot } from "../../../components/Wrappers/OverlaySlot";
import type {
  SurveyAttempt,
  SurveySession,
  SurveySessionSnapshot,
} from "../../../session/SurveySession";
import {
  CompletionControl,
  type CompletionLayout,
} from "../../CompletionControl";
import type {
  HostCapabilities,
  QuestionArrangement,
  QuestionResponseFlow,
} from "../../contracts";
import type {
  QuestionGroupHost,
  QuestionHostFrameProps,
} from "../../QuestionFrameShared";
import { SessionQuestionSlot, type AttemptDraftState } from "../../sessionSlot";

export type SurveyHostProps = {
  session: SurveySession;
  progress?: React.ReactNode;
  arrangement: QuestionArrangement;
  capabilities: HostCapabilities;
  view?: string;
  onInspect?: (value: {
    definition: unknown;
    draft: unknown;
    validation: unknown;
    transcript?: unknown;
  }) => void;
  onRedirect?: (url: string) => void;
  draftState?: AttemptDraftState | null;
  setDraftState?: Dispatch<SetStateAction<AttemptDraftState | null>>;
};

export const navigateToRedirect = (url: string) => {
  globalThis.location.href = url;
};

const WebviewHandoff = ({
  content,
  label,
}: {
  content: string;
  label: string;
}) => {
  return (
    <OverlaySlot open ariaLabelledBy="webview-handoff-title">
      <Box
        data-testid="session-control-webview_modal"
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <Typography id="webview-handoff-title">{content}</Typography>
        <Button
          variant="contained"
          onClick={() => globalThis.open(globalThis.location.href, "_blank")}
        >
          {label}
        </Button>
      </Box>
    </OverlaySlot>
  );
};

const optionLabel = (attempt: SurveyAttempt): string | undefined => {
  const option = attempt.question.question.options?.[0];
  return typeof option === "object" && "text" in option
    ? option.text
    : undefined;
};

export const SurveyAttemptContent = ({
  session,
  snapshot,
  Frame,
  GroupFrame,
  arrangement,
  preferredResponseFlow,
  capabilities,
  view,
  onInspect,
  onRedirect = navigateToRedirect,
  promptInHistory = false,
  completionLayout,
  history,
  draftState,
  setDraftState,
}: SurveyHostProps & {
  snapshot: SurveySessionSnapshot;
  Frame: ComponentType<QuestionHostFrameProps>;
  GroupFrame?: QuestionGroupHost;
  preferredResponseFlow?: QuestionResponseFlow;
  promptInHistory?: boolean;
  completionLayout: CompletionLayout;
  history?: React.ReactNode;
}) => {
  const { globalObject } = useThemeContext();
  const theme = globalObject.themeData.normalizedTheme;
  const attempt = snapshot.attempt;
  if (!attempt) return null;
  if (attempt.question.question.type === "webview_modal") {
    return (
      <WebviewHandoff
        content={attempt.question.question.content}
        label={optionLabel(attempt) ?? theme.copy.actions.openInBrowser}
      />
    );
  }
  if (attempt.question.question.type === "complete") {
    return (
      <CompletionControl
        key={attempt.id}
        layout={completionLayout}
        content={attempt.question.question.content}
        redirect={attempt.question.redirect}
        numberSeconds={attempt.question.question.number_seconds}
        startedAtMs={attempt.startedAtMs}
        buttonLabel={optionLabel(attempt)}
        active={snapshot.phase === "active" && !snapshot.error}
        canUndo={snapshot.canUndo}
        onUndo={() => session.undoPrevious(attempt)}
        onRedirect={onRedirect}
      />
    );
  }
  return (
    <SessionQuestionSlot
      session={session}
      Frame={Frame}
      GroupFrame={GroupFrame}
      arrangement={arrangement}
      preferredResponseFlow={preferredResponseFlow}
      capabilities={capabilities}
      view={view}
      onInspect={onInspect}
      promptInHistory={promptInHistory}
      history={history}
      draftState={draftState}
      setDraftState={setDraftState}
    />
  );
};
